// ============================================================================
// useSettings.js - Central Settings Hook for BRIO Atlas
// ============================================================================
// Loads settings from Supabase, caches in memory, and provides helpers:
//   - timezone
//   - currency (base, display, FX rates, conversion, formatting)
//   - project categories (active, ordered)
//   - theme config (CSS variable injection, including team colours)
//   - general / financial / invoicing / calendar settings
//   - FX rate auto-refresh (edge function integration)
//   - audit logging
// ============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { setActiveMbtiColors } from '../utils/personalityHelpers';

// ============================================================================
// CONSTANTS & DEFAULTS
// ============================================================================

const DEFAULT_TIMEZONE = 'Europe/Amsterdam';
const DEFAULT_BASE_CURRENCY = 'INR';
const DEFAULT_DISPLAY_CURRENCY = 'INR';
const DEFAULT_FX_RATE = 90.0; // Fallback EUR-INR rate

// ── Database export paging ──────────────────────────────────────────────────
// PostgREST caps every response at the project's "Max rows" setting (1000 by
// default), so a plain .select('*') silently returns the first page and calls
// it the whole table. time_entries alone holds ~67k rows, which made the
// export look like it worked while dropping 98% of the data. Every table is
// now paged to its exact row count, and a short read raises instead of
// passing for a complete export.
const EXPORT_PAGE_SIZE = 1000;

// Offset paging needs a deterministic sort or rows repeat or vanish between
// pages, so each fetch appends its primary key as a tiebreaker. Default is
// `id`; these six are keyed on something else.
const EXPORT_PK = {
  app_settings:           ['key'],
  fee_factor_definitions: ['factor_key'],
  inflation_cpi_yearly:   ['country_code', 'year'],
  location_geocodes:      ['query'],
  project_geocodes:       ['project_id'],
  team_projects:          ['team_id', 'project_id'],
};

// Tables deliberately capped rather than exported whole.
const EXPORT_MAX_ROWS = {
  settings_audit_log: 1000, // newest 1000 only; it is an append-only log
};

// Pages one table to completion. The count runs under the caller's RLS, so
// the target is what this user is allowed to see, not what the table holds.
async function fetchAllRows(tableName, buildQuery) {
  const pk = EXPORT_PK[tableName] || ['id'];
  const maxRows = EXPORT_MAX_ROWS[tableName];

  const { count, error: countError } = await supabase
    .from(tableName)
    .select('*', { count: 'exact', head: true });
  if (countError) throw countError;

  const total = count || 0;
  const target = maxRows ? Math.min(total, maxRows) : total;
  const rows = [];

  // Each pass starts where the last one actually ended, so a server that
  // serves fewer rows than asked (a "Max rows" setting below EXPORT_PAGE_SIZE)
  // costs extra round trips but still yields the whole table.
  while (rows.length < target) {
    const want = Math.min(EXPORT_PAGE_SIZE, target - rows.length);
    let query = buildQuery();
    pk.forEach(col => { query = query.order(col); });
    const { data, error } = await query.range(rows.length, rows.length + want - 1);
    if (error) throw error;
    // An empty page while rows are still owed means the server stopped
    // serving them: rows deleted mid-export, or RLS narrowing under us.
    // Break rather than spin, and fail on the check below.
    if (!data || data.length === 0) break;
    rows.push(...data);
  }

  if (rows.length < target) {
    throw new Error(
      `read ${rows.length} of ${target} rows; the table changed during export or RLS hid rows partway`
    );
  }
  return rows;
}

// Currency display config
const CURRENCY_CONFIG = {
  EUR: { symbol: '\u20AC', locale: 'nl-NL', position: 'prefix' },
  INR: { symbol: '\u20B9', locale: 'en-IN', position: 'prefix' }
};

// Defaults for new general/financial/invoicing/calendar settings
const DEFAULT_ROUNDING_INTERVAL = 15;
const DEFAULT_LOCALE = 'nl-NL';
const DEFAULT_ESSENTIAL_PROFIT_PCT = 20;
const DEFAULT_DORMANCY_POOL_PCT = 3; // % of monthly office overhead reserved for dormancy carrying cost
const DEFAULT_INVOICE_DATE_RULES = { ruleType: 'manual', dayOfMonth: 1 };
const DEFAULT_PAID_CONFIRMATION_MODE = 'manual';
const DEFAULT_CALENDAR_AUTO_SYNC = { enabled: false, frequency: 'daily', syncRangeDays: 30 };

// Default hiring candidate roles / categories
// Stored in app_settings under key 'candidateRoles' as a JSON array of strings.
// Display uses Title Case; storage is original casing for backward compat.
const DEFAULT_CANDIDATE_ROLES = [
  'Architecture', 'Interior Designer', 'Model Maker',
  'Graphic Designer', 'HR', 'Admin', 'PR'
];

// Default priority options for project tasks and inquiries.
// Stored in app_settings under key 'priorityOptions' as a JSON array of strings.
const DEFAULT_PRIORITY_OPTIONS = ['Urgent', 'High', 'Medium', 'Low'];

// Default task categories for the Project Tasks (status tracker) category column.
// Stored in app_settings under key 'taskCategories' as a JSON array of { name, color }.
const DEFAULT_TASK_CATEGORIES = [
  { name: 'Follow up',         color: '#7B6B3A' },
  { name: 'BRIO',              color: '#9f902a' },
  { name: 'Client',            color: '#4A7A9A' },
  { name: 'Structure',         color: '#5A7A8A' },
  { name: 'MEP',               color: '#8B5E3C' },
  { name: 'Landscape',         color: '#5C8A5A' },
  { name: 'Consultants',       color: '#7A6890' },
  { name: 'Vendors',           color: '#8A5A7A' },
  { name: 'Site coordination', color: '#6A6A8A' },
  { name: 'PMC',               color: '#934948' },
  { name: 'MOM_Linked',        color: '#6B5EA8' }
];

// Default 20-color team palette (desaturated, professional colors)
const DEFAULT_TEAM_PALETTE = [
  '#5B7A99', '#CC9966', '#6B9B7B', '#B36B6B', '#8B7BA3',
  '#9B8B7A', '#7A9B9B', '#9B7A8B', '#8B9B7A', '#9B7A7A',
  '#6B8B8B', '#8B6B7A', '#7A8B6B', '#9B6B8B', '#6B7A9B',
  '#8B9B6B', '#7A6B8B', '#9B8B6B', '#6B9B8B', '#8B7A6B'
];

// Settings keys that are too large to ship on every settings load.
//
// useSettings() is mounted by many pages, and each mount used to pull the whole
// app_settings table — so these keys (hundreds of kB each, and growing as
// scopes and templates are added) were re-sent to every client every time.
// They are excluded from the initial fetch and loaded on demand instead, by
// the few pages that actually read them, via loadHeavySetting().
//
// Writes are unaffected: updateSetting() still upserts to app_settings and
// merges the new value into appSettings locally.
const HEAVY_SETTING_KEYS = [
  'contract_scopes',
  'timeline_templates',
  'scope_templates',
  'statementAutoRules'
];

// ============================================================================
// HOOK: useSettings
// ============================================================================
export function useSettings() {
  // --- State ---
  const [appSettings, setAppSettings] = useState({});
  const [fxRates, setFxRates] = useState([]);
  const [cpiYearly, setCpiYearly] = useState([]);
  const [costIndexYearly, setCostIndexYearly] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [themeConfig, setThemeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialLoadDone = useRef(false);
  // Which HEAVY_SETTING_KEYS have finished loading. Consumers need this to tell
  // "not fetched yet" apart from "fetched, and the key genuinely has no value".
  const [heavySettingsLoaded, setHeavySettingsLoaded] = useState({});
  const heavyLoads = useRef({});

  // ========================================================================
  // FETCH: Load all settings from Supabase
  // ========================================================================
  const fetchAll = useCallback(async () => {
    try {
      setError(null);

      const [settingsRes, fxRes, categoriesRes, themeRes, cpiRes, costIdxRes] = await Promise.all([
        supabase.from('app_settings').select('*')
          .not('key', 'in', `(${HEAVY_SETTING_KEYS.join(',')})`),
        supabase.from('fx_rates_monthly').select('*').order('month', { ascending: false }),
        supabase.from('project_categories').select('*').order('sort_order'),
        supabase.from('color_schemes').select('*').eq('is_active', true).maybeSingle(),
        supabase.from('inflation_cpi_yearly').select('*').order('country_code').order('year', { ascending: false }),
        supabase.schema('boq').from('cost_index_yearly').select('*').order('region').order('year', { ascending: false })
      ]);

      // Process app_settings into a key-value map
      if (settingsRes.error) {
        console.warn('[useSettings] app_settings fetch error:', settingsRes.error);
      } else {
        const map = {};
        (settingsRes.data || []).forEach(row => { map[row.key] = row.value; });
        // Keep any heavy key already loaded. This fetch never carries them, and
        // loadHeavySetting() runs alongside it and caches its request, so a heavy
        // key that lands first would otherwise be wiped and never fetched again.
        setAppSettings(prev => {
          const next = { ...map };
          HEAVY_SETTING_KEYS.forEach(key => {
            if (Object.prototype.hasOwnProperty.call(prev, key)) next[key] = prev[key];
          });
          return next;
        });
      }

      // FX rates
      if (fxRes.error) {
        console.warn('[useSettings] fx_rates_monthly fetch error:', fxRes.error);
      } else {
        setFxRates(fxRes.data || []);
      }

      // Project categories
      if (categoriesRes.error) {
        console.warn('[useSettings] project_categories fetch error:', categoriesRes.error);
      } else {
        setProjectCategories((categoriesRes.data || []).map(c => ({
          id: c.id,
          label: c.label,
          order: c.sort_order,
          isActive: c.is_active,
          updatedAt: c.updated_at
        })));
      }

      // Theme
      if (!themeRes.error && themeRes.data) {
        setThemeConfig(themeRes.data.config || null);
      }

      // Inflation CPI (general consumer index — fallback / fee inflation)
      if (cpiRes.error) {
        console.warn('[useSettings] inflation_cpi_yearly fetch error:', cpiRes.error);
      } else {
        setCpiYearly(cpiRes.data || []);
      }

      // Construction cost index (used to escalate BOQ library rates)
      if (costIdxRes.error) {
        console.warn('[useSettings] boq.cost_index_yearly fetch error:', costIdxRes.error);
      } else {
        setCostIndexYearly(costIdxRes.data || []);
      }
    } catch (err) {
      console.error('[useSettings] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, []);

  // Initial load
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ========================================================================
  // FETCH: Load one heavy setting on demand
  // ========================================================================
  // Call this from a page that reads one of HEAVY_SETTING_KEYS. Safe to call on
  // every render — the in-flight promise is cached per key, so repeated calls
  // (and several components asking for the same key) share one request.
  const loadHeavySetting = useCallback(async (key) => {
    if (!HEAVY_SETTING_KEYS.includes(key)) return;
    if (heavyLoads.current[key]) return heavyLoads.current[key];

    const request = (async () => {
      const { data, error: loadErr } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();

      if (loadErr) {
        console.warn(`[useSettings] ${key} fetch error:`, loadErr);
        // Drop the cached promise so a later mount can retry.
        delete heavyLoads.current[key];
        return;
      }

      // data is null when the key does not exist yet — record that as loaded so
      // consumers stop waiting and fall back to their defaults.
      setAppSettings(prev => ({ ...prev, [key]: data ? data.value : null }));
      setHeavySettingsLoaded(prev => ({ ...prev, [key]: true }));
    })();

    heavyLoads.current[key] = request;
    return request;
  }, []);

  // ========================================================================
  // DERIVED: Timezone
  // ========================================================================
  const timezone = useMemo(() => {
    const val = appSettings.timezone;
    return (typeof val === 'string' && val.length > 0) ? val : DEFAULT_TIMEZONE;
  }, [appSettings.timezone]);

  // ========================================================================
  // DERIVED: Currency
  // ========================================================================
  const baseCurrency = useMemo(() => {
    const val = appSettings.baseCurrency;
    return (val === 'EUR' || val === 'INR') ? val : DEFAULT_BASE_CURRENCY;
  }, [appSettings.baseCurrency]);

  const displayCurrency = useMemo(() => {
    const val = appSettings.displayCurrency;
    return (val === 'EUR' || val === 'INR') ? val : DEFAULT_DISPLAY_CURRENCY;
  }, [appSettings.displayCurrency]);

  const fxDefaultRate = useMemo(() => {
    const val = appSettings.fxDefaultEurInr;
    const num = parseFloat(val);
    return Number.isFinite(num) && num > 0 ? num : DEFAULT_FX_RATE;
  }, [appSettings.fxDefaultEurInr]);

  // ========================================================================
  // DERIVED: General / Financial / Invoicing / Calendar settings
  // ========================================================================
  const roundingInterval = useMemo(() => {
    const val = appSettings.roundingInterval;
    const num = typeof val === 'number' ? val : parseInt(val, 10);
    return Number.isFinite(num) && num > 0 ? num : DEFAULT_ROUNDING_INTERVAL;
  }, [appSettings.roundingInterval]);

  const defaultLocale = useMemo(() => {
    const val = appSettings.defaultLocale;
    return (typeof val === 'string' && val.length > 0) ? val : DEFAULT_LOCALE;
  }, [appSettings.defaultLocale]);

  const essentialProfitPercentage = useMemo(() => {
    const val = appSettings.essentialProfitPercentage;
    const num = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(num) ? num : DEFAULT_ESSENTIAL_PROFIT_PCT;
  }, [appSettings.essentialProfitPercentage]);

  const dormancyPoolPct = useMemo(() => {
    const val = appSettings.dormancyPoolPct;
    const num = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(num) && num >= 0 && num <= 100 ? num : DEFAULT_DORMANCY_POOL_PCT;
  }, [appSettings.dormancyPoolPct]);

  const manualSalaryMarkup = useMemo(() => {
    const val = appSettings.manualSalaryMarkup;
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { /* fall through */ }
    }
    return {};
  }, [appSettings.manualSalaryMarkup]);

  const invoiceDateRules = useMemo(() => {
    const val = appSettings.invoiceDateRules;
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { /* fall through */ }
    }
    return DEFAULT_INVOICE_DATE_RULES;
  }, [appSettings.invoiceDateRules]);

  const paidConfirmationMode = useMemo(() => {
    const val = appSettings.paidConfirmationMode;
    return (typeof val === 'string' && val.length > 0) ? val : DEFAULT_PAID_CONFIRMATION_MODE;
  }, [appSettings.paidConfirmationMode]);

  const calendarAutoSync = useMemo(() => {
    const val = appSettings.calendarAutoSync;
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { /* fall through */ }
    }
    return DEFAULT_CALENDAR_AUTO_SYNC;
  }, [appSettings.calendarAutoSync]);

  const wheelPickerSpeed = useMemo(() => {
    const val = appSettings.wheelPickerSpeed;
    const num = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(num) && num > 0 && num <= 1 ? num : 0.5;
  }, [appSettings.wheelPickerSpeed]);

  // Site Visit: configurable backend hours (default 10)
  const siteVisitBackendHours = useMemo(() => {
    const val = appSettings.siteVisitBackendHours;
    const num = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(num) && num > 0 ? num : 10;
  }, [appSettings.siteVisitBackendHours]);

  // ========================================================================
  // DERIVED: Candidate Roles / Hiring Categories
  // ========================================================================
  // Stored as JSON array of strings in app_settings key 'candidateRoles'.
  // Falls back to DEFAULT_CANDIDATE_ROLES if not set.
  // ========================================================================
  const candidateRoles = useMemo(() => {
    const val = appSettings.candidateRoles;
    if (Array.isArray(val) && val.length > 0) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* fall through */ }
    }
    return DEFAULT_CANDIDATE_ROLES;
  }, [appSettings.candidateRoles]);

  // ========================================================================
  // DERIVED: Priority Options (admin-editable list of task/inquiry priorities)
  // ========================================================================
  // Stored as JSON array of strings in app_settings key 'priorityOptions'.
  // Falls back to DEFAULT_PRIORITY_OPTIONS if not set.
  // ========================================================================
  const priorityOptions = useMemo(() => {
    const val = appSettings.priorityOptions;
    if (Array.isArray(val) && val.length > 0) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* fall through */ }
    }
    return DEFAULT_PRIORITY_OPTIONS;
  }, [appSettings.priorityOptions]);

  // ========================================================================
  // DERIVED: Task Categories (admin-editable list for the Project Tasks category column)
  // ========================================================================
  // Stored as JSON array of { name, color } in app_settings key 'taskCategories'.
  // Falls back to DEFAULT_TASK_CATEGORIES if not set.
  // ========================================================================
  const taskCategories = useMemo(() => {
    const val = appSettings.taskCategories;
    if (Array.isArray(val) && val.length > 0) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* fall through */ }
    }
    return DEFAULT_TASK_CATEGORIES;
  }, [appSettings.taskCategories]);

  // ========================================================================
  // DERIVED: Excel Export Styles (admin-editable font styles for XLSX exports)
  // ========================================================================
  // Stored as JSON object in app_settings key 'excelExportStyles'.
  // Shape: { style1: { fontFamily, fontSize }, style2: ..., style3: ... }
  // Falls back to defaults defined in src/utils/excelStyles.js.
  // ========================================================================
  const DEFAULT_EXCEL_EXPORT_STYLES = {
    style1: { fontFamily: 'Itcavantgardestd-bk', fontSize: 10 },
    style2: { fontFamily: 'Itcavantgardestd-bk', fontSize: 12 },
    style3: { fontFamily: 'Itcavantgardestd-bk', fontSize: 16 },
  };
  const excelExportStyles = useMemo(() => {
    const val = appSettings.excelExportStyles;
    if (val && typeof val === 'object' && !Array.isArray(val)) return { ...DEFAULT_EXCEL_EXPORT_STYLES, ...val };
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_EXCEL_EXPORT_STYLES, ...parsed };
      } catch { /* fall through */ }
    }
    return DEFAULT_EXCEL_EXPORT_STYLES;
  }, [appSettings.excelExportStyles]);

  // ========================================================================
  // DERIVED: Scope PDF Styles (admin-editable font styles for SOW PDF export)
  // ========================================================================
  // Stored as JSON object in app_settings key 'scopePdfStyles'.
  // Shape: { body: { fontFamily, fontSize }, heading: ..., title: ..., notes: ... }
  // ========================================================================
  const DEFAULT_SCOPE_PDF_STYLES = {
    body:    { fontFamily: 'Raleway', fontSize: 11 },
    heading: { fontFamily: 'Raleway', fontSize: 12 },
    title:   { fontFamily: 'Raleway', fontSize: 18 },
    notes:   { fontFamily: 'Raleway', fontSize: 10 },
  };
  const scopePdfStyles = useMemo(() => {
    const val = appSettings.scopePdfStyles;
    if (val && typeof val === 'object' && !Array.isArray(val)) return { ...DEFAULT_SCOPE_PDF_STYLES, ...val };
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_SCOPE_PDF_STYLES, ...parsed };
      } catch { /* fall through */ }
    }
    return DEFAULT_SCOPE_PDF_STYLES;
  }, [appSettings.scopePdfStyles]);

  // ========================================================================
  // DERIVED: Email Templates (admin-editable overrides for timeline emails)
  // ========================================================================
  // Loaded from the email_templates table (migrated from app_settings JSON).
  // Shape: { stage_initiated: { subject, body }, stage_approved: ..., ... }
  // Returns null when no custom templates exist (falls back to built-in defaults).
  // ========================================================================
  const [emailTemplates, setEmailTemplates] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchEmailTemplates = async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('template_type, subject, body');
      if (cancelled || error || !data || data.length === 0) return;
      const map = {};
      data.forEach(row => {
        if (row.subject || row.body) {
          map[row.template_type] = { subject: row.subject || '', body: row.body || '' };
        }
      });
      if (!cancelled) {
        setEmailTemplates(Object.keys(map).length > 0 ? map : null);
      }
    };
    fetchEmailTemplates();
    return () => { cancelled = true; };
  }, []);

  // ========================================================================
  // DERIVED: Invoice Branding (office-specific bank details + header images,
  // global signature block)
  // ========================================================================
  // Stored as JSON in app_settings key 'invoiceBranding'.
  // Structure: { offices: { rotterdam: {...}, mumbai: {...} }, signatureBlock: {...} }
  // Backward compat: migrates legacy bankDetails/headerImages into offices.rotterdam.
  // ========================================================================
  const invoiceBranding = useMemo(() => {
    // Not all of these are bank details: `pan`, `vatOrGst`, `udyam` and `kvk`
    // are company registration numbers that share the invoice footer block.
    // Each office fills only the ones it is registered under, and the renderer
    // drops the empty ones, which is what keeps KvK off an Indian invoice and
    // Udyam off a Dutch one.
    const blankBank = {
      accountName: '', accountNumber: '', bankName: '', branch: '',
      ifsc: '', swift: '', iban: '', pan: '', vatOrGst: '', kvk: '', udyam: ''
    };
    // Images carry `url` (Storage object, current) or `dataUrl` (inline base64,
    // pre-migration). Read them through resolveBrandingImageSrc.
    const blankOffice = (label) => ({
      label,
      headerImage: { url: '', dataUrl: '', filename: '' },
      bankDetails: { ...blankBank }
    });
    const defaults = {
      offices: {
        rotterdam: blankOffice('NL'),
        mumbai: blankOffice('IN')
      },
      signatureBlock: {
        companyLine: '', signatoryName: '', signatoryTitle: '',
        signatureImage: { url: '', dataUrl: '', filename: '' }
      },
      // Reusable list of SAC codes for the invoice editor's SAC picker.
      // Each entry: { code, label }. Global (not office-specific).
      sacCodes: []
    };

    const merge = (raw) => {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return defaults;

      // Backward compat: migrate legacy flat bankDetails / headerImages
      // into offices.rotterdam if the new offices key doesn't exist yet.
      let offices = raw.offices;
      if (!offices || typeof offices !== 'object') {
        offices = {
          rotterdam: {
            ...blankOffice('NL'),
            bankDetails: { ...blankBank, ...raw.bankDetails },
            headerImage: raw.headerImages?.rotterdam || blankOffice('NL').headerImage,
          },
          mumbai: {
            ...blankOffice('IN'),
            headerImage: raw.headerImages?.mumbai || blankOffice('IN').headerImage,
          }
        };
        // If legacy bankDetails had 'gst', map it to 'vatOrGst'
        if (raw.bankDetails?.gst && !offices.rotterdam.bankDetails.vatOrGst) {
          offices.rotterdam.bankDetails.vatOrGst = raw.bankDetails.gst;
        }
      }

      return {
        offices: {
          rotterdam: {
            label: offices.rotterdam?.label || 'NL',
            headerImage: { ...defaults.offices.rotterdam.headerImage, ...offices.rotterdam?.headerImage },
            bankDetails: { ...blankBank, ...offices.rotterdam?.bankDetails }
          },
          mumbai: {
            label: offices.mumbai?.label || 'IN',
            headerImage: { ...defaults.offices.mumbai.headerImage, ...offices.mumbai?.headerImage },
            bankDetails: { ...blankBank, ...offices.mumbai?.bankDetails }
          }
        },
        signatureBlock: { ...defaults.signatureBlock, ...raw.signatureBlock },
        sacCodes: Array.isArray(raw.sacCodes)
          ? raw.sacCodes
              .filter(x => x && typeof x === 'object' && !Array.isArray(x))
              .map(x => ({ code: String(x.code ?? '').trim(), label: String(x.label ?? '').trim() }))
          : []
      };
    };

    const val = appSettings.invoiceBranding;
    if (val && typeof val === 'object' && !Array.isArray(val)) return merge(val);
    if (typeof val === 'string') {
      try { return merge(JSON.parse(val)); } catch { /* fall through */ }
    }
    return defaults;
  }, [appSettings.invoiceBranding]);

  // ========================================================================
  // FX RATE MAP: month string -> rounded rate (2 decimals)
  // ========================================================================
  const fxRateMap = useMemo(() => {
    const map = new Map();
    fxRates.forEach(r => {
      // Key: YYYY-MM (from YYYY-MM-01 or YYYY-MM-DDT...)
      const monthKey = (r.month || '').substring(0, 7);
      if (monthKey && r.avg_rate != null) {
        // CRITICAL: Round to 2 decimals for computation
        map.set(monthKey, Math.round(parseFloat(r.avg_rate) * 100) / 100);
      }
    });
    return map;
  }, [fxRates]);

  // Sorted month keys for fallback lookup (descending)
  const fxMonthKeysSorted = useMemo(() => {
    return Array.from(fxRateMap.keys()).sort().reverse();
  }, [fxRateMap]);

  // ========================================================================
  // HELPER: getFxRateForDate(date)
  // ========================================================================
  // Rules:
  //   1. Exact month match -> use that rate
  //   2. No match -> nearest previous month
  //   3. No prior month -> fxDefaultEurInr from settings
  // Always returns 2-decimal rounded rate.
  // ========================================================================
  const getFxRateForDate = useCallback((date) => {
    if (!date) return Math.round(fxDefaultRate * 100) / 100;

    // Parse date to YYYY-MM
    let targetMonth;
    if (typeof date === 'string') {
      targetMonth = date.substring(0, 7);
    } else if (date instanceof Date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      targetMonth = `${y}-${m}`;
    } else {
      return Math.round(fxDefaultRate * 100) / 100;
    }

    // 1. Exact match
    if (fxRateMap.has(targetMonth)) {
      return fxRateMap.get(targetMonth);
    }

    // 2. Nearest previous month
    for (const key of fxMonthKeysSorted) {
      if (key < targetMonth) {
        return fxRateMap.get(key);
      }
    }

    // 3. Default fallback
    return Math.round(fxDefaultRate * 100) / 100;
  }, [fxRateMap, fxMonthKeysSorted, fxDefaultRate]);

  // ========================================================================
  // HELPER: convertMoney(amount, fromCurrency, toCurrency, date)
  // ========================================================================
  // Uses 2-decimal rounded FX rate for the transaction's month.
  // ========================================================================
  const convertMoney = useCallback((amount, fromCurrency, toCurrency, date) => {
    if (!Number.isFinite(amount)) return 0;
    if (fromCurrency === toCurrency) return amount;

    const rate = getFxRateForDate(date);

    if (fromCurrency === 'EUR' && toCurrency === 'INR') {
      return amount * rate;
    }
    if (fromCurrency === 'INR' && toCurrency === 'EUR') {
      return rate > 0 ? amount / rate : 0;
    }

    // Unsupported pair - return unchanged
    console.warn(`[useSettings] Unsupported FX conversion: ${fromCurrency} -> ${toCurrency}`);
    return amount;
  }, [getFxRateForDate]);

  // ========================================================================
  // HELPER: formatMoney(amount, currency?, options?)
  // ========================================================================
  // Uses displayCurrency if currency not specified.
  // Rounds to nearest 100 for INR (existing BRIO behavior), nearest 1 for EUR.
  // Pass { exact: true } for expense claims and other amounts that must
  // match the exact figure to be reimbursed — bypasses tiered rounding and
  // shows full 2-decimal precision.
  // ========================================================================
  const formatMoney = useCallback((amount, currency, options) => {
    const curr = currency || displayCurrency;
    const config = CURRENCY_CONFIG[curr] || CURRENCY_CONFIG.INR;

    if (!Number.isFinite(amount)) amount = 0;

    let rounded;
    let decimals;

    if (options && options.exact) {
      rounded = Math.round(amount * 100) / 100;
      decimals = 2;
    } else {
      const abs = Math.abs(amount);
      if (abs < 10) {
        rounded = Math.round(amount * 100) / 100;
        decimals = 2;
      } else if (abs < 1000) {
        rounded = Math.round(amount / 10) * 10;
        decimals = 0;
      } else {
        rounded = Math.round(amount / 100) * 100;
        decimals = 0;
      }
    }

    const formatted = rounded.toLocaleString(config.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    return `${config.symbol}${formatted}`;
  }, [displayCurrency]);

  // ========================================================================
  // HELPER: formatMoneyInDisplayCurrency(amount, sourceCurrency, date, options?)
  // ========================================================================
  const formatMoneyInDisplayCurrency = useCallback((amount, sourceCurrency, date, options) => {
    const converted = convertMoney(amount, sourceCurrency || baseCurrency, displayCurrency, date);
    return formatMoney(converted, displayCurrency, options);
  }, [convertMoney, formatMoney, baseCurrency, displayCurrency]);

  // ========================================================================
  // DERIVED: Active project categories (ordered)
  // ========================================================================
  const activeCategories = useMemo(() => {
    return projectCategories
      .filter(c => c.isActive)
      .sort((a, b) => a.order - b.order);
  }, [projectCategories]);

  const activeCategoryLabels = useMemo(() => {
    return activeCategories.map(c => c.label);
  }, [activeCategories]);

  // ========================================================================
  // DERIVED: Team Palette (20 colors for team assignment)
  // ========================================================================
  // Returns the teamPalette from themeConfig, or falls back to DEFAULT_TEAM_PALETTE.
  // This palette is used in the Add/Edit Team modal to choose a team color.
  // ========================================================================
  const teamPalette = useMemo(() => {
    const palette = themeConfig?.teamPalette;
    if (Array.isArray(palette) && palette.length === 20) {
      return palette;
    }
    return DEFAULT_TEAM_PALETTE;
  }, [themeConfig]);

  // ========================================================================
  // THEME: Apply CSS variables from theme config on load/change
  // Now also injects team colour CSS variables.
  // ========================================================================
  useEffect(() => {
    if (!themeConfig) return;

    const root = document.documentElement;

    // Role colors
    if (themeConfig.roles) {
      const roleVarMap = {
        'Director':          '--brio-role-director',
        'Partner':           '--brio-role-partner',
        'Associate':         '--brio-role-associate',
        'Senior Architect':  '--brio-role-senior-architect',
        'Project Architect': '--brio-role-project-architect',
        'Medior Architect':  '--brio-role-medior-architect',
        'Junior Architect':  '--brio-role-junior-architect',
        'Intern':            '--brio-role-intern',
        'Support':           '--brio-role-support'
      };
      Object.entries(themeConfig.roles).forEach(([role, color]) => {
        const varName = roleVarMap[role];
        if (varName && color) {
          root.style.setProperty(varName, color);
        }
      });
    }

    // Category palettes
    if (themeConfig.categoryPalettes) {
      Object.entries(themeConfig.categoryPalettes).forEach(([cat, colors]) => {
        if (Array.isArray(colors) && colors.length > 0) {
          root.style.setProperty(`--brio-${cat}-palette`, colors.join(','));
        }
      });
    }

    // Team colours → CSS variables --brio-team-<slug>
    if (themeConfig.teams) {
      Object.entries(themeConfig.teams).forEach(([teamName, color]) => {
        if (teamName && color) {
          // Slug: lowercase, replace spaces/special chars with hyphens
          const slug = teamName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          root.style.setProperty(`--brio-team-${slug}`, color);
        }
      });
    }

    // MBTI personality colours → activate runtime overrides
    setActiveMbtiColors(themeConfig.mbtiColors || null);
  }, [themeConfig]);

  // ========================================================================
  // AUDIT LOGGING
  // ========================================================================
  // BUG FIX: logAudit is now declared BEFORE all write operations that
  // reference it. Previously, updateSetting (a const) referenced logAudit
  // before it was initialized due to const not being hoisted.
  // Audit logging is intentionally non-fatal: a failed audit insert
  // must never prevent a settings update from succeeding.
  // ========================================================================
  const logAudit = useCallback(async (action, payload) => {
    try {
      await supabase.from('settings_audit_log').insert([{
        actor_user_id: (await supabase.auth.getUser())?.data?.user?.id || null,
        action,
        payload
      }]);
    } catch (err) {
      console.warn('[useSettings] Audit log error (non-fatal):', err);
    }
  }, []);

  // ========================================================================
  // WRITE: Update a single app_setting
  // ========================================================================
  // Supports both primitive values and JSON objects/arrays.
  // JSON values are stored directly (Supabase jsonb column handles it).
  // ========================================================================
  const updateSetting = useCallback(async (key, value) => {
    try {
      const { error: upsertErr } = await supabase
        .from('app_settings')
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });

      if (upsertErr) throw upsertErr;

      // Update local state immediately
      setAppSettings(prev => ({ ...prev, [key]: value }));

      // Audit log (non-fatal)
      try { await logAudit('SETTINGS_UPDATE', { key, value }); } catch (_) { /* non-fatal */ }

      return { success: true };
    } catch (err) {
      console.error('[useSettings] updateSetting error:', err);
      return { success: false, error: err.message };
    }
  }, [logAudit]);

  // ========================================================================
  // WRITE: Candidate Roles (convenience wrapper around updateSetting)
  // ========================================================================
  const saveCandidateRoles = useCallback(async (roles) => {
    return await updateSetting('candidateRoles', roles);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Priority Options (convenience wrapper around updateSetting)
  // ========================================================================
  const savePriorityOptions = useCallback(async (options) => {
    return await updateSetting('priorityOptions', options);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Task Categories (convenience wrapper around updateSetting)
  // ========================================================================
  const saveTaskCategories = useCallback(async (categories) => {
    return await updateSetting('taskCategories', categories);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Invoice Branding (convenience wrapper around updateSetting)
  // ========================================================================
  const saveInvoiceBranding = useCallback(async (branding) => {
    return await updateSetting('invoiceBranding', branding);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Excel Export Styles (convenience wrapper around updateSetting)
  // ========================================================================
  const saveExcelExportStyles = useCallback(async (styles) => {
    return await updateSetting('excelExportStyles', styles);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Scope PDF Styles (convenience wrapper around updateSetting)
  // ========================================================================
  const saveScopePdfStyles = useCallback(async (styles) => {
    return await updateSetting('scopePdfStyles', styles);
  }, [updateSetting]);

  // ========================================================================
  // READ: SharePoint Storage Config
  // Stored as JSON in app_settings key 'sharepointStorage'.
  // Structure: { siteId, driveId, siteName, libraryName, baseFolderPath }
  // ========================================================================
  const sharepointStorage = useMemo(() => {
    const raw = appSettings.sharepointStorage;
    const defaults = { siteId: '', driveId: '', siteName: '', libraryName: '', baseFolderPath: '' };
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return defaults;
    return { ...defaults, ...raw };
  }, [appSettings.sharepointStorage]);

  // WRITE: SharePoint Storage Config
  const saveSharepointStorage = useCallback(async (config) => {
    return await updateSetting('sharepointStorage', config);
  }, [updateSetting]);

  // ========================================================================
  // READ: Dropbox Storage Config
  // Stored as JSON in app_settings key 'dropboxConfig'.
  // Structure: { accessToken, refreshToken, appKey, appSecret, baseFolderPath }
  // ========================================================================
  const dropboxConfig = useMemo(() => {
    const raw = appSettings.dropboxConfig;
    const defaults = { accessToken: '', refreshToken: '', appKey: '', appSecret: '', baseFolderPath: '' };
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return defaults;
    return { ...defaults, ...raw };
  }, [appSettings.dropboxConfig]);

  // WRITE: Dropbox Storage Config
  const saveDropboxConfig = useCallback(async (config) => {
    return await updateSetting('dropboxConfig', config);
  }, [updateSetting]);

  // ========================================================================
  // WRITE: Email Templates — now managed by useSupabaseEmailTemplates hook
  // and the EmailTemplatesTab in AccountingSettingsPage. The saveEmailTemplates
  // function is kept as a no-op for backward compatibility.
  // ========================================================================
  const saveEmailTemplates = useCallback(async () => {
    return { success: true };
  }, []);

  // ========================================================================
  // WRITE: FX Rates CRUD
  // ========================================================================
  const createFxRate = useCallback(async (rateData) => {
    try {
      const { data, error: insertErr } = await supabase
        .from('fx_rates_monthly')
        .insert([{
          month: rateData.month,
          pair: rateData.pair || 'EURINR',
          avg_rate: rateData.avg_rate,
          notes: rateData.notes || null
        }])
        .select()
        .single();

      if (insertErr) throw insertErr;

      await logAudit('FX_RATE_CREATE', { month: rateData.month, avg_rate: rateData.avg_rate });
      await fetchAll();
      return { success: true, data };
    } catch (err) {
      console.error('[useSettings] createFxRate error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const updateFxRate = useCallback(async (id, updates) => {
    try {
      const dbUpdates = {};
      if (updates.month !== undefined) dbUpdates.month = updates.month;
      if (updates.avg_rate !== undefined) dbUpdates.avg_rate = updates.avg_rate;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

      const { error: updateErr } = await supabase
        .from('fx_rates_monthly')
        .update(dbUpdates)
        .eq('id', id);

      if (updateErr) throw updateErr;

      await logAudit('FX_RATE_UPDATE', { id, ...updates });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] updateFxRate error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const deleteFxRate = useCallback(async (id) => {
    try {
      const { error: deleteErr } = await supabase
        .from('fx_rates_monthly')
        .delete()
        .eq('id', id);

      if (deleteErr) throw deleteErr;

      await logAudit('FX_RATE_DELETE', { id });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] deleteFxRate error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // WRITE: Inflation CPI (public.inflation_cpi_yearly) — general consumer
  // index, kept as a labelled fallback. PK is (country_code, year), so we
  // upsert on those rather than an id.
  // ========================================================================
  const upsertCpiYear = useCallback(async (entry) => {
    try {
      const row = {
        country_code: entry.country_code,
        year: entry.year,
        cpi: entry.cpi,
        source: entry.source || 'manual',
      };
      const { error: upErr } = await supabase
        .from('inflation_cpi_yearly')
        .upsert(row, { onConflict: 'country_code,year' });
      if (upErr) throw upErr;
      await logAudit('CPI_UPSERT', row);
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] upsertCpiYear error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const deleteCpiYear = useCallback(async (countryCode, year) => {
    try {
      const { error: delErr } = await supabase
        .from('inflation_cpi_yearly')
        .delete()
        .eq('country_code', countryCode)
        .eq('year', year);
      if (delErr) throw delErr;
      await logAudit('CPI_DELETE', { country_code: countryCode, year });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] deleteCpiYear error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // WRITE: Construction cost index (boq.cost_index_yearly) — the series used
  // to inflation-adjust BOQ library rates. UNIQUE (series, region, year).
  // ========================================================================
  const upsertCostIndexYear = useCallback(async (entry) => {
    try {
      const row = {
        series: entry.series || 'construction',
        region: entry.region,
        year: entry.year,
        index_value: entry.index_value,
        source: entry.source || 'manual',
        note: entry.note || null,
      };
      const { error: upErr } = await supabase
        .schema('boq')
        .from('cost_index_yearly')
        .upsert(row, { onConflict: 'series,region,year' });
      if (upErr) throw upErr;
      await logAudit('COST_INDEX_UPSERT', row);
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] upsertCostIndexYear error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const deleteCostIndexYear = useCallback(async (id) => {
    try {
      const { error: delErr } = await supabase
        .schema('boq')
        .from('cost_index_yearly')
        .delete()
        .eq('id', id);
      if (delErr) throw delErr;
      await logAudit('COST_INDEX_DELETE', { id });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] deleteCostIndexYear error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // FX RATE AUTO-REFRESH: Call the Supabase Edge Function
  // ========================================================================
  // mode: 'backfill' (Jan 2018 → current) or 'incremental' (last month).
  // The Edge Function handles all API calls server-side to avoid exposing
  // any API keys in the browser.
  // ========================================================================
  const refreshFxRates = useCallback(async ({ mode = 'incremental' } = {}) => {
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('fx_rates_refresh', {
        body: { mode }
      });

      if (fnErr) throw fnErr;

      // Re-fetch local state with the new data
      await fetchAll();

      await logAudit('FX_RATES_REFRESH', { mode, result: data });
      return { success: true, data };
    } catch (err) {
      console.error('[useSettings] refreshFxRates error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // WRITE: Project Categories CRUD
  // ========================================================================
  const createCategory = useCallback(async (label) => {
    try {
      const maxOrder = projectCategories.reduce((max, c) => Math.max(max, c.order || 0), 0);
      const { data, error: insertErr } = await supabase
        .from('project_categories')
        .insert([{ label, sort_order: maxOrder + 1, is_active: true }])
        .select()
        .single();

      if (insertErr) throw insertErr;

      await logAudit('CATEGORY_CREATE', { label });
      await fetchAll();
      return { success: true, data };
    } catch (err) {
      console.error('[useSettings] createCategory error:', err);
      return { success: false, error: err.message };
    }
  }, [projectCategories, fetchAll, logAudit]);

  const updateCategory = useCallback(async (id, updates) => {
    try {
      const dbUpdates = {};
      if (updates.label !== undefined) dbUpdates.label = updates.label;
      if (updates.sort_order !== undefined) dbUpdates.sort_order = updates.sort_order;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

      const { error: updateErr } = await supabase
        .from('project_categories')
        .update(dbUpdates)
        .eq('id', id);

      if (updateErr) throw updateErr;

      await logAudit('CATEGORY_UPDATE', { id, ...updates });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] updateCategory error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const deleteCategory = useCallback(async (id) => {
    try {
      const { error: deleteErr } = await supabase
        .from('project_categories')
        .delete()
        .eq('id', id);

      if (deleteErr) throw deleteErr;

      await logAudit('CATEGORY_DELETE', { id });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] deleteCategory error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  const reorderCategories = useCallback(async (orderedIds) => {
    try {
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index + 1
      }));

      for (const upd of updates) {
        const { error } = await supabase
          .from('project_categories')
          .update({ sort_order: upd.sort_order })
          .eq('id', upd.id);
        if (error) throw error;
      }

      await logAudit('CATEGORY_REORDER', { orderedIds });
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useSettings] reorderCategories error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // WRITE: Theme Config
  // ========================================================================
  const updateThemeConfig = useCallback(async (newConfig) => {
    try {
      const { data: existing } = await supabase
        .from('color_schemes')
        .select('id')
        .eq('is_active', true)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('color_schemes')
          .update({ config: newConfig })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('color_schemes')
          .insert([{ name: 'Custom', config: newConfig, is_active: true }]);
        if (error) throw error;
      }

      setThemeConfig(newConfig);
      await logAudit('THEME_UPDATE', { keys: Object.keys(newConfig) });
      return { success: true };
    } catch (err) {
      console.error('[useSettings] updateThemeConfig error:', err);
      return { success: false, error: err.message };
    }
  }, [logAudit]);

  // ========================================================================
  // EXPORT / IMPORT
  // ========================================================================
  const exportSettings = useCallback(async (selectedTables) => {
    try {
      const exportData = {
        version: '1.0.0',
        exportDate: new Date().toISOString(),
        schemaVersion: 1,
        tables: {}
      };

      const tableMap = {
        // Settings & Configuration
        app_settings:              () => supabase.from('app_settings').select('*'),
        fx_rates_monthly:          () => supabase.from('fx_rates_monthly').select('*').order('month'),
        project_categories:        () => supabase.from('project_categories').select('*').order('sort_order'),
        color_schemes:             () => supabase.from('color_schemes').select('*'),
        holidays:                  () => supabase.from('holidays').select('*').order('date'),
        leave_policies:            () => supabase.from('leave_policies').select('*'),
        notice_policies:           () => supabase.from('notice_policies').select('*'),
        inflation_cpi_yearly:      () => supabase.from('inflation_cpi_yearly').select('*').order('year'),
        email_templates:           () => supabase.from('email_templates').select('*'),
        radar_settings:            () => supabase.from('radar_settings').select('*'),
        // Capped at 1000 via EXPORT_MAX_ROWS, not .limit(), so paging owns the
        // row window; .limit() and .range() on one query fight each other.
        settings_audit_log:        () => supabase.from('settings_audit_log').select('*').order('created_at', { ascending: false }),
        // Users & Team
        users:                     () => supabase.from('users').select('*').order('name'),
        user_profiles:             () => supabase.from('user_profiles').select('*'),
        user_addresses:            () => supabase.from('user_addresses').select('*'),
        user_education:            () => supabase.from('user_education').select('*'),
        user_employers:            () => supabase.from('user_employers').select('*'),
        user_role_history:         () => supabase.from('user_role_history').select('*'),
        user_salary_history:       () => supabase.from('user_salary_history').select('*'),
        user_performance_reviews:  () => supabase.from('user_performance_reviews').select('*'),
        user_evaluation_reports:   () => supabase.from('user_evaluation_reports').select('*'),
        user_review_criteria:      () => supabase.from('user_review_criteria').select('*'),
        user_review_agreements:    () => supabase.from('user_review_agreements').select('*'),
        user_notes:                () => supabase.from('user_notes').select('*'),
        teams:                     () => supabase.from('teams').select('*'),
        team_projects:             () => supabase.from('team_projects').select('*'),
        employee_leave_entitlements: () => supabase.from('employee_leave_entitlements').select('*'),
        // Clients & Projects
        clients:                   () => supabase.from('clients').select('*').order('name'),
        client_addresses:          () => supabase.from('client_addresses').select('*'),
        client_attributes:         () => supabase.from('client_attributes').select('*'),
        client_people:             () => supabase.from('client_people').select('*'),
        projects:                  () => supabase.from('projects').select('*').order('name'),
        project_geocodes:          () => supabase.from('project_geocodes').select('*'),
        location_geocodes:         () => supabase.from('location_geocodes').select('*'),
        project_account_names:     () => supabase.from('project_account_names').select('*'),
        project_stage_status:      () => supabase.from('project_stage_status').select('*'),
        project_stage_entries:     () => supabase.from('project_stage_entries').select('*'),
        tasks:                     () => supabase.from('tasks').select('*'),
        address_directory:         () => supabase.from('address_directory').select('*'),
        // Time Tracking & Leave
        time_entries:              () => supabase.from('time_entries').select('*').order('date', { ascending: false }),
        time_off_requests:         () => supabase.from('time_off_requests').select('*').order('start_date', { ascending: false }),
        leave_audit_log:           () => supabase.from('leave_audit_log').select('*').order('created_at', { ascending: false }),
        calendar_events:           () => supabase.from('calendar_events').select('*'),
        tt_allocations:            () => supabase.from('tt_allocations').select('*'),
        // Invoicing & Fees
        invoices:                  () => supabase.from('invoices').select('*').order('invoice_date', { ascending: false }),
        invoice_files:             () => supabase.from('invoice_files').select('*'),
        fee_revisions:             () => supabase.from('fee_revisions').select('*'),
        fee_factor_definitions:    () => supabase.from('fee_factor_definitions').select('*'),
        fee_inquiry_models:        () => supabase.from('fee_inquiry_models').select('*'),
        fee_inquiry_model_factors: () => supabase.from('fee_inquiry_model_factors').select('*'),
        fee_inquiry_model_comparables: () => supabase.from('fee_inquiry_model_comparables').select('*'),
        // Salary & Accounting
        salary_monthly_ledger:     () => supabase.from('salary_monthly_ledger').select('*').order('month', { ascending: false }),
        salary_driver_events:      () => supabase.from('salary_driver_events').select('*'),
        salary_matrix_entries:     () => supabase.from('salary_matrix_entries').select('*'),
        salary_matrix_history:     () => supabase.from('salary_matrix_history').select('*'),
        salary_matrix_history_nl:  () => supabase.from('salary_matrix_history_nl').select('*'),
        salary_import_batches:     () => supabase.from('salary_import_batches').select('*'),
        salary_import_staging:     () => supabase.from('salary_import_staging').select('*'),
        expenses:                  () => supabase.from('expenses').select('*').order('date', { ascending: false }),
        office_overheads_monthly:  () => supabase.from('office_overheads_monthly').select('*'),
        office_overheads_yearly:   () => supabase.from('office_overheads_yearly').select('*'),
        stage_window_ledger:       () => supabase.from('stage_window_ledger').select('*'),
        // Planning (HFB)
        weekly_team_assignments:       () => supabase.from('weekly_team_assignments').select('*'),
        weekly_project_allocations:    () => supabase.from('weekly_project_allocations').select('*'),
        weekly_project_role_targets:   () => supabase.from('weekly_project_role_targets').select('*'),
        project_weekly_tasks:          () => supabase.from('project_weekly_tasks').select('*'),
        project_weekly_subtasks:       () => supabase.from('project_weekly_subtasks').select('*'),
        capacity_gap_monthly:          () => supabase.from('capacity_gap_monthly').select('*'),
        forecast_runs:                 () => supabase.from('forecast_runs').select('*'),
        // Hiring
        hiring_candidates:         () => supabase.from('hiring_candidates').select('*'),
        hiring_interviews:         () => supabase.from('hiring_interviews').select('*'),
        hiring_recommendations:    () => supabase.from('hiring_recommendations').select('*'),
        // Inquiries
        inquiries:                 () => supabase.from('inquiries').select('*'),
        inquiry_followups:         () => supabase.from('inquiry_followups').select('*'),
        inquiry_intake_messages:   () => supabase.from('inquiry_intake_messages').select('*'),
        // Radar & Analytics
        radar_item_status:         () => supabase.from('radar_item_status').select('*'),
        recommended_actions:       () => supabase.from('recommended_actions').select('*'),
        // Integrations
        bank_payment_events:       () => supabase.from('bank_payment_events').select('*'),
        email_template_usage_log:  () => supabase.from('email_template_usage_log').select('*'),
      };

      // Failures are collected rather than swallowed. The previous `if (!error)`
      // left a failed table out of the file entirely and still reported
      // success, so an export missing a table was indistinguishable from one
      // where the table was empty.
      const failures = [];

      for (const tableName of selectedTables) {
        if (!tableMap[tableName]) {
          failures.push({ table: tableName, message: 'no query defined for this table' });
          continue;
        }
        try {
          exportData.tables[tableName] = await fetchAllRows(tableName, tableMap[tableName]);
        } catch (err) {
          failures.push({ table: tableName, message: err?.message || String(err) });
        }
      }

      exportData.stats = {};
      Object.entries(exportData.tables).forEach(([table, rows]) => {
        exportData.stats[table] = rows.length;
      });
      // Recorded in the file too, so a partial export stays self-describing
      // once it is off this machine.
      exportData.failures = failures;

      await logAudit('EXPORT', { tables: selectedTables, stats: exportData.stats, failures });
      return { success: true, data: exportData, failures };
    } catch (err) {
      console.error('[useSettings] exportSettings error:', err);
      return { success: false, error: err.message };
    }
  }, [logAudit]);

  const importSettings = useCallback(async (importData, mode = 'merge') => {
    try {
      if (!importData?.tables) throw new Error('Invalid import file: missing tables key');

      const summary = { inserts: 0, updates: 0, errors: [] };

      for (const [tableName, rows] of Object.entries(importData.tables)) {
        if (!rows || !Array.isArray(rows) || rows.length === 0) continue;

        if (mode === 'replace') {
          // Delete existing before insert
          const { error: delErr } = await supabase
            .from(tableName)
            .delete()
            .neq(tableName === 'app_settings' ? 'key' : 'id', '__never_match__');
          if (delErr) {
            summary.errors.push(`${tableName} delete: ${delErr.message}`);
            continue;
          }
        }

        const { error: upsertErr } = await supabase.from(tableName).upsert(rows);
        if (upsertErr) {
          summary.errors.push(`${tableName} upsert: ${upsertErr.message}`);
        } else {
          summary.inserts += rows.length;
        }
      }

      await logAudit(mode === 'merge' ? 'IMPORT_MERGE' : 'IMPORT_REPLACE', {
        tables: Object.keys(importData.tables),
        stats: summary
      });

      await fetchAll();

      return { success: true, summary };
    } catch (err) {
      console.error('[useSettings] importSettings error:', err);
      return { success: false, error: err.message };
    }
  }, [fetchAll, logAudit]);

  // ========================================================================
  // RETURN
  // ========================================================================
  return {
    loading,
    error,
    refetch: fetchAll,

    // Raw app_settings map (for hooks that need direct key access).
    // Keys in HEAVY_SETTING_KEYS are absent until loadHeavySetting() fetches
    // them; heavySettingsLoaded says which have arrived.
    appSettings,
    loadHeavySetting,
    heavySettingsLoaded,

    // Timezone
    timezone,

    // Currency
    baseCurrency,
    displayCurrency,
    fxRates,
    fxRateMap,
    fxDefaultRate,

    // Inflation CPI (general consumer index — fallback / fee inflation)
    cpiYearly,
    upsertCpiYear,
    deleteCpiYear,

    // Construction cost index (BOQ rate escalation source)
    costIndexYearly,
    upsertCostIndexYear,
    deleteCostIndexYear,

    // Helpers
    getFxRateForDate,
    convertMoney,
    formatMoney,
    formatMoneyInDisplayCurrency,

    // Project Categories
    projectCategories,
    activeCategories,
    activeCategoryLabels,

    // Theme
    themeConfig,
    teamPalette,

    // General / Financial / Invoicing / Calendar derived getters
    roundingInterval,
    defaultLocale,
    essentialProfitPercentage,
    dormancyPoolPct,
    manualSalaryMarkup,
    invoiceDateRules,
    paidConfirmationMode,
    calendarAutoSync,
    wheelPickerSpeed,
    siteVisitBackendHours,

    // Priority Options
    priorityOptions,
    savePriorityOptions,

    // Task Categories (Project Tasks category column)
    taskCategories,
    saveTaskCategories,

    // Hiring / Candidate Roles
    candidateRoles,
    saveCandidateRoles,

    // Invoice Branding (bank details, signature, header images)
    invoiceBranding,
    saveInvoiceBranding,

    // Excel Export Styles (font styles for XLSX exports)
    excelExportStyles,
    saveExcelExportStyles,

    // Scope PDF Styles (font styles for SOW PDF exports)
    scopePdfStyles,
    saveScopePdfStyles,

    // SharePoint Storage Config (site/drive IDs for invoice upload)
    sharepointStorage,
    saveSharepointStorage,

    // Dropbox Storage Config (reimbursement receipt upload)
    dropboxConfig,
    saveDropboxConfig,

    // Email Templates (admin-editable overrides for timeline emails)
    emailTemplates,
    saveEmailTemplates,

    // Write operations
    updateSetting,
    createFxRate,
    updateFxRate,
    deleteFxRate,
    refreshFxRates,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    updateThemeConfig,

    // Export/Import
    exportSettings,
    importSettings,

    // Audit
    logAudit
  };
}

export default useSettings;
